import { computed, inject, resource } from "@angular/core";
import { signalStore, withComputed, withMethods, withProps } from "@ngrx/signals";
import { LoginStore } from "../../stores/login.store";
import { S25Util } from "../../util/s25-util";
import { DEFAULT_THEME_BRANDING, loadConnect25Branding, type ThemeBranding } from "./theme-branding";

/**
 * Root signal store for Connect25 appearance (system preference JSON).
 *
 * **Reuse:** For another product or pref, add a sibling store that calls
 * `loadThemeBrandingPreference(prefName, prefType)` from `theme-branding.ts`, or
 * generalize this store with an injection token for `{ prefName, prefType }`.
 */
export const SeConnect25ThemeStore = signalStore(
    { providedIn: "root" },
    withProps(() => ({
        _loginStore: inject(LoginStore),
    })),
    withProps((store) => ({
        _resource: resource({
            /** Refetch when the signed-in user changes */
            params: () => store._loginStore.user()?.userId ?? 0,
            loader: () => loadConnect25Branding(),
        }),
    })),
    withComputed((store) => ({
        /** Defined only after the first successful load (use for form hydration). */
        loadedValue: computed((): ThemeBranding | undefined =>
            store._resource.hasValue() ? (store._resource.value() as ThemeBranding) : undefined,
        ),
        /** Safe for banners and hero UI while loading (falls back to defaults). */
        theme: computed((): ThemeBranding => {
            const v = store._resource.value() as ThemeBranding | undefined;
            return store._resource.hasValue() && v ? v : DEFAULT_THEME_BRANDING;
        }),
        loaded: computed(() => store._resource.hasValue()),
        isLoading: computed(() => store._resource.isLoading()),
        error: computed(() => store._resource.error()),
    })),
    withComputed((store) => ({
        productTitle: computed(() => store.theme().productTitle),
        logoFileRef: computed(() => store.theme().logoFileRef),
        bannerBackground: computed(() => store.theme().bannerBackground),
        bannerAccent: computed(() => store.theme().bannerAccent),
        bannerForeground: computed(() => store.theme().bannerForeground),
        slideshowGradientStart: computed(() => store.theme().slideshowGradientStart),
        slideshowGradientEnd: computed(() => store.theme().slideshowGradientEnd),
        supportEmail: computed(() => store.theme().supportEmail),
    })),
    withMethods((store) => ({
        async ensureLoaded(): Promise<void> {
            if (!store._resource.hasValue()) {
                store._resource.reload();
                await S25Util.waitFor(() => store._resource.hasValue(), 50);
            }
        },
        reload() {
            return store._resource.reload();
        },
    })),
);
export type SeConnect25ThemeStore = InstanceType<typeof SeConnect25ThemeStore>;
